/* eslint-disable max-classes-per-file */
import {
  type VirtualElement,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";
import { Attrs } from "prosemirror-model";
import type { Selection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { t } from "i18n/i18n";

/*
The contents of this file are originally based on `prompt.ts` from the `prosemirror-example-setup` repo,
but have been heavily modified to suit our needs.
https://github.com/ProseMirror/prosemirror-example-setup/blob/master/src/prompt.ts
*/

const prefix = "ProseMirror-popover";

/**
 * Creates a VirtualElement for positioning the popover at the selection
 */
export function createSelectionElement(view: EditorView, selection: Selection): VirtualElement {
  return {
    getBoundingClientRect() {
      const coords = view.coordsAtPos(selection.head);

      return {
        x: coords.left,
        y: coords.top,
        width: coords.right - coords.left,
        height: coords.bottom - coords.top,
        left: coords.left,
        right: coords.right,
        top: coords.top,
        bottom: coords.bottom,
      };
    },
  };
}

/**
 * Opens a popover near the given selection element.
 */
export function openPopover(options: {
  appendTo?: HTMLElement;
  selectionEl: VirtualElement;
  render: (close: () => void) => HTMLElement;
  focusEl?: HTMLElement;
  onClose?: () => void;
}) {
  const appendTo = options.appendTo || document.body;
  const wrapper = appendTo.appendChild(document.createElement("div"));
  wrapper.className = prefix;

  let autoUpdateCleanup: (() => void) | undefined;
  let isClosing = false;

  const mouseOutside = (e: MouseEvent) => {
    if (!wrapper.contains(e.target as HTMLElement)) close();
  };
  setTimeout(() => window.addEventListener("mousedown", mouseOutside), 50);
  let close = () => {
    if (isClosing) return;
    isClosing = true;
    window.removeEventListener("mousedown", mouseOutside);
    if (autoUpdateCleanup) autoUpdateCleanup();
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    if (options.onClose) {
      options.onClose();
    }
  };

  wrapper.appendChild(options.render(close));

  let hasFocused = false;
  autoUpdateCleanup = autoUpdate(options.selectionEl, wrapper, () => {
    computePosition(options.selectionEl, wrapper, {
      placement: "bottom",
      middleware: [
        offset(3),
        flip({
          crossAxis: false,
          padding: {
            top: 40,
          },
        }),
        shift({
          padding: {
            top: 3,
            bottom: 3,
            left: 3,
            right: 3,
          },
        }),
      ],
    }).then((position) => {
      wrapper.style.left = `${position.x}px`;
      wrapper.style.top = `${position.y}px`;
      if (options.focusEl && !hasFocused) {
        hasFocused = true;
        options.focusEl.focus();
      }
    });
  });
}

/**
 * Opens a popover near the given selection element,
 * with a form containing the given fields.
 */
export function openPopoverForm(options: {
  appendTo?: HTMLElement;
  selectionEl: VirtualElement;
  title: string;
  fields: { [name: string]: Field };
  callback: (attrs: Attrs) => void;
  onClose?: () => void;
}) {
  const domFields: HTMLElement[] = [];
  for (const field of Object.values(options.fields)) {
    domFields.push(field.render());
  }

  return openPopover({
    appendTo: options.appendTo,
    selectionEl: options.selectionEl,
    onClose: options.onClose,
    render: (close) => {
      const createWrapper = () => {
        const form = document.createElement("form");
        let wrapper: HTMLElement = form;
        if (Object.keys(options.fields).length === 1) {
          wrapper = document.createElement("div");
          wrapper.className =
            "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:w-[500px]";
          form.appendChild(wrapper);
        }

        const error = form.appendChild(document.createElement("div"));
        error.className = `${prefix}-invalid hidden`;

        const submit = () => {
          form.dataset.submitted = "true";
          const params = getValues(options.fields, domFields);
          if (params) {
            close();
            options.callback(params);
          }
        };

        form.addEventListener("submit", (e) => {
          e.preventDefault();
          submit();
        });

        form.addEventListener("keydown", (e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            close();
          } else if (e.key === "Enter" && !(e.ctrlKey || e.metaKey || e.shiftKey)) {
            e.preventDefault();
            submit();
          } else if (e.key === "Tab") {
            window.setTimeout(() => {
              if (!form.contains(document.activeElement)) close();
            }, 500);
          }
        });

        return { form, wrapper };
      };

      const addFields = (wrapper: HTMLElement) => {
        const fields = document.createElement("div");
        fields.className = "flex gap-2 flex-col items-start sm:flex-row sm:items-center flex-1";

        wrapper.appendChild(fields);

        if (options.title) {
          fields.appendChild(document.createElement("h5")).textContent = options.title;
        }
        domFields.forEach((field) => {
          const fieldWrapper = document.createElement("div");
          fieldWrapper.className = "flex-1";
          fieldWrapper.appendChild(field);
          fields.appendChild(fieldWrapper);
        });
      };

      const addButtons = (wrapper: HTMLElement) => {
        const submitButton = document.createElement("button");
        submitButton.type = "submit";
        submitButton.className = `${prefix}-submit`;
        submitButton.textContent = t("common.ok");
        const cancelButton = document.createElement("button");
        cancelButton.type = "button";
        cancelButton.className = `${prefix}-cancel`;
        cancelButton.textContent = t("common.cancel");
        cancelButton.addEventListener("click", close);

        const buttons = wrapper.appendChild(document.createElement("div"));
        buttons.className = `${prefix}-buttons`;
        buttons.appendChild(submitButton);
        buttons.appendChild(document.createTextNode(" "));
        buttons.appendChild(cancelButton);
      };

      const { form, wrapper } = createWrapper();
      addFields(wrapper);
      addButtons(wrapper);

      return form;
    },
    focusEl: domFields[0],
  });
}

function getValues<F extends { [name: string]: Field }>(
  fields: F,
  domFields: readonly HTMLElement[],
): Record<keyof F, string | null> | null {
  const result = Object.create(null) as Record<keyof F, string | null>;
  let i = 0;
  for (const name in fields) {
    if (Object.hasOwn(fields, name)) {
      const field = fields[name];
      const dom = domFields[i];
      i += 1;
      const value = field.read(dom);
      const bad = field.validate(value);
      if (bad) {
        reportInvalid(dom, bad);
        return null;
      }
      result[name] = value;
    }
  }
  return result;
}

function reportInvalid(dom: HTMLElement, message: string) {
  const wrapper = dom.closest(`.${prefix}`);
  if (!wrapper) return;
  const error = wrapper.querySelector(`.${prefix}-invalid`);
  if (!error) return;
  error.textContent = message;
  error.classList.remove("hidden");
  // eslint-disable-next-line no-param-reassign
  dom.ariaInvalid = "true";
  // eslint-disable-next-line no-param-reassign
  dom.ariaErrorMessageElements = [error];
}

function clearInvalid(dom: HTMLElement) {
  const form = dom.closest("form") as HTMLElement | null;
  if (!form || form.dataset.submitted !== "true") {
    return;
  }
  const wrapper = form.closest(`.${prefix}`);
  if (!wrapper) return;
  const error = wrapper.querySelector(`.${prefix}-invalid`);
  if (!error) return;
  error.textContent = "";
  error.classList.add("hidden");
  // eslint-disable-next-line no-param-reassign
  dom.ariaInvalid = "false";
  // eslint-disable-next-line no-param-reassign
  dom.ariaErrorMessageElements = [];
}

/// The type of field that `openPopoverForm` expects to be passed to it.
export abstract class Field {
  /// Create a field with the given options. Options support by all
  /// field types are:
  // eslint-disable-next-line no-useless-constructor
  constructor(
    /// @internal
    readonly options: {
      /// Used in the `name` attribute of the field's DOM node.
      name: string;

      /// The starting value for the field.
      value?: string;

      /// The label for the field.
      label: string;

      /// Placeholder text for the field.
      placeholder?: string;

      /// Whether the field is required.
      required?: boolean;

      /// A function to transform the value before it is validated.
      transform?: (value: string | null) => string | null;

      /// A function to validate the given value. Should return an
      /// error message if it is not valid.
      validate?: (value: string | null) => string | null;
    },
    // eslint-disable-next-line no-empty-function
  ) {}

  /// Render the field to the DOM. Should be implemented by all subclasses.
  abstract render(): HTMLElement;

  /// Read the field's value from its DOM node.
  read(dom: HTMLElement) {
    const value = (dom as HTMLInputElement).value;
    if (this.options.transform) {
      return this.options.transform(value);
    }
    return value;
  }

  /// A field-type-specific validation function.
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  validateType(value: string | null): string | null {
    return null;
  }

  /// @internal
  validate(value: string | null): string | null {
    if (!value && this.options.required) return t("validation.required");
    return (
      this.validateType(value) || (this.options.validate ? this.options.validate(value) : null)
    );
  }
}

/// A field class for single-line text fields.
export class TextField extends Field {
  render() {
    const input = document.createElement("input");
    input.type = "text";
    if (this.options.name) {
      input.name = this.options.name;
    }
    if (this.options.label) {
      input.ariaLabel = this.options.label;
    }
    if (this.options.placeholder) {
      input.placeholder = this.options.placeholder;
    }
    input.value = this.options.value || "";
    input.autocomplete = "off";

    input.onkeydown = () => clearInvalid(input);

    return input;
  }
}
